import React from 'react';
import { getIntl } from '../../../utils/intl';
import Text from '../../v2BaseComponents/Text';
import Stack from '../../v2BaseComponents/Stack';
import { Image, TouchableOpacity, View, StyleSheet } from 'react-native';
import Images from '../../../Images';
import Alert from '../../v2BaseComponents/Alert';
import { useProfilesTabNavigation } from '../../../utils/appNavigationNames';
import useRecordEvent from '../../../hooks/useRecordEvent';
import { Card } from '../../v2BaseComponents/Card';
import { usePortalConnectUsesFastenFlow } from '../../../hooks/api/usePortalConnectUsesFastenFlow';

const intl = getIntl('no-connected-systems-gate', {
    header: 'Connect to Patient Portals',
    description:
        'Primary Record partners with Fasten to securely connect patient portals, so medical records come together automatically in one place.',
    subtext: 'You stay in control and can disconnect anytime.',
    contentMessage: 'Choose your preferred method to connect to patient portals.',
    oneUpMessage: 'Our partner, 1up Health, provides secure access to nearly 8,000 Patient Portals.',
    infoAlert: 'Permissions required to connect to a patient portal',
    infoAlertContent:
        'You do not have permission to connect to a patient portal. Please contact the profile owner for access.',
});

export type NoConnectedSystemsGateOwnProps = {
    medicalProfileId: string;
    canAdd: boolean;
};

export type NoConnectedSystemsGateProps = NoConnectedSystemsGateOwnProps;

const NoConnectedSystemsGate: React.FC<NoConnectedSystemsGateProps> = (props) => {
    const { showFastenConnectUi } = usePortalConnectUsesFastenFlow(props.medicalProfileId);
    const navigation = useProfilesTabNavigation();
    const recordEvent = useRecordEvent();

    if (!props.canAdd) {
        return (
            <Stack vertical space={30} fullWidth justify="flex-start">
                <Stack vertical space={4} fullWidth justify="flex-start">
                    <Text variant="h4">{intl('header')}</Text>
                    <Alert type="info" title={intl('infoAlert')} message={intl('infoAlertContent')} />
                </Stack>
            </Stack>
        );
    }

    if (showFastenConnectUi) {
        return (
            <Stack vertical space={16} fullWidth align="center">
                <Text variant="h3" style={styles.header}>
                    {intl('header')}
                </Text>

                <View style={styles.illustrationContainer}>
                    <Image
                        alt="Patient Portal Connection"
                        source={Images.Puzzle}
                        defaultSource={Images.Puzzle}
                        resizeMode="contain"
                        style={styles.puzzleImage}
                    />
                    <View style={styles.fastenBrandingContainer}>
                        <Text variant="h3" style={styles.fastenText}>
                            fasten
                        </Text>
                    </View>
                </View>

                <Stack vertical space={8} align="center" style={styles.textContainer}>
                    <Text variant="body-1" style={styles.description}>
                        {intl('description')}
                    </Text>
                    <Text variant="body-2" color="Gray-Text" style={styles.subtext}>
                        {intl('subtext')}
                    </Text>
                </Stack>
            </Stack>
        );
    }

    // Legacy 1upHealth flow
    const handleOneUpPress = () => {
        navigation.navigate('add-portal', {
            profileId: props.medicalProfileId,
        });
        recordEvent('searchPatientPortals', { source: 'no-connected-gate' });
    };

    return (
        <Stack vertical space={30} fullWidth justify="flex-start">
            <Stack vertical space={4} fullWidth justify="flex-start">
                <Text variant="h4">{intl('header')}</Text>
                <Stack horizontal justify="center" fullWidth>
                    <Image
                        alt="Available Patient Portals"
                        source={Images.Puzzle}
                        defaultSource={Images.Puzzle}
                        resizeMode="contain"
                        style={{ width: 164, height: 164 }}
                    />
                </Stack>
                <Text variant="body-1">{intl('contentMessage')}</Text>

                <Stack vertical space={16} fullWidth style={{ marginTop: 16 }}>
                    <TouchableOpacity onPress={handleOneUpPress} activeOpacity={0.7}>
                        <Card>
                            <Stack vertical space={12} fullWidth>
                                <Stack horizontal justify="center" fullWidth>
                                    <Image
                                        alt="1upHealth Logo"
                                        source={Images.OneUpLogo}
                                        defaultSource={Images.OneUpLogo}
                                        resizeMode="contain"
                                        style={{ width: 130, height: 30 }}
                                    />
                                </Stack>
                                <Text variant="body-2" style={{ textAlign: 'center' }}>
                                    {intl('oneUpMessage')}
                                </Text>
                            </Stack>
                        </Card>
                    </TouchableOpacity>
                </Stack>
            </Stack>
        </Stack>
    );
};

const styles = StyleSheet.create({
    header: {
        textAlign: 'center',
    },
    illustrationContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: -8,
    },
    puzzleImage: {
        width: 180,
        height: 140,
        marginBottom: 20,
    },
    fastenBrandingContainer: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    fastenText: {
        fontSize: 28,
        fontFamily: 'NeometricAltExtraBold',
        color: '#6366F1',
        letterSpacing: 1,
    },
    textContainer: {
        maxWidth: 400,
        paddingHorizontal: 16,
    },
    description: {
        textAlign: 'center',
    },
    subtext: {
        textAlign: 'center',
        fontStyle: 'italic',
    },
});

export default React.memo(NoConnectedSystemsGate);
